import { API_ENDPOINT } from '@Env/variables';
import { createAxiosInstance } from './axios';
import { isAxiosError } from 'axios';
import {
  Assigned,
  AssignedRecordResponse,
  AssignedRecordsResponse,
  JiraProject,
  JiraIssue,
  JiraSavedProjectResponse,
  JiraVersion,
} from '@Models/types';

const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const jiraAPI = (logout: () => void) => {
  const axios = createAxiosInstance(logout, API_ENDPOINT);

  return {
    getVersions: async (projectKey: string) => {
      const res = await axios.get<JiraVersion[]>('/api/getVersions', {
        params: { projectKey },
      });
      return res.data;
    },

    getProjects: async () => {
      const res = await axios.get<{ projects: JiraProject[] }>(
        '/api/jira/getProjects'
      );
      return res.data.projects;
    },

    getFavProjects: async () => {
      const res = await axios.get<{ FavProjects: JiraProject[] }>(
        '/api/jira/getFavProjects'
      );
      return res.data.FavProjects;
    },

    saveFavProjects: async (favoriteProjectIds: JiraProject[]) => {
      const res = await axios.post<JiraSavedProjectResponse>(
        '/api/jira/setFavProjects',
        {
          favoriteProjectIds,
        }
      );
      return res.data.favorites;
    },

    getIssueList: async (projectKey: string) => {
      let allIssues: JiraIssue[] = [];
      let currentNextPageToken: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        try {
          const res: { data: { issues: JiraIssue[]; nextPageToken?: string } } =
            await axios.get('/api/jira/getIssueList', {
              params: {
                project_key: projectKey,
                nextPageToken: currentNextPageToken,
              },
            });

          allIssues = allIssues.concat(res.data.issues);

          if (res.data.nextPageToken) {
            currentNextPageToken = res.data.nextPageToken;
            await delay(500); // Wait 500ms before requesting the next page to prevent hitting rate limits
          } else {
            hasMore = false;
          }
        } catch (error) {
          if (isAxiosError(error) && error.response?.status === 429) {
            const rawRetryAfter = error.response.data?.retryAfter;
            const parsedRetryAfter = Number(rawRetryAfter);
            const retrySecs =
              Number.isFinite(parsedRetryAfter) && parsedRetryAfter > 0
                ? parsedRetryAfter
                : 5;
            console.warn(`Jira rate limit hit. Waiting ${retrySecs}s...`);
            await delay(retrySecs * 1000);
            continue;
          }
          throw error;
        }
      }

      return allIssues;
    },

    getIssueDetails: async (issueKey: string) => {
      const res = await axios.get<JiraIssue>('/api/jira/getIssueDetails', {
        params: { issue_key: issueKey },
      });
      return res.data;
    },

    getAssignedRecords: async () => {
      let assigned: Assigned[] = [];
      const email = localStorage.getItem('email') || '';
      const res = await axios.get<AssignedRecordsResponse>(
        `/api/getJiraAssignedRecords?email=${email}`
      );

      assigned = res.data.assigned.map((record: AssignedRecordResponse) => ({
        reference_num: record.reference_num,
        name: record.name,
        status: record.status,
        workflow_status: record.workflow_status
          ? {
              id: record.workflow_status.id,
              name: record.workflow_status.name,
              position: record.workflow_status.position,
              complete: record.workflow_status.complete,
              color: record.workflow_status.color,
            }
          : undefined,
        record_type: record.record_type,
        id: record.id,
        description: record.description || 'No description available',
        priority: record.priority || 'No priority set',
        url: record.url,
        attachment: (
          record as AssignedRecordResponse & {
            attachment?: {
              id: string;
              filename: string;
              mimeType: string;
              content: string;
            }[];
          }
        ).attachment,
      }));

      return assigned;
    },
  };
};
